import React, { memo, useState } from 'react'
import { Container, Row, Col, Form } from 'react-bootstrap'
import { Paper, Stack, IconButton, Tooltip, Grid, Typography, TextField, Button, FormGroup, FormControlLabel, Checkbox } from '@mui/material'
import { motion } from 'framer-motion'
import configServ from '../../../services/config'
import { useEffect } from 'react'
import SaveIcon from '@mui/icons-material/Save';
import UpdateIcon from '@mui/icons-material/Update';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import AlertDialog from "../../AlertDialog/AlertDialog";
import { useDispatch, useSelector } from 'react-redux'
import { triggerLoader } from '../../../redux/reducers/LoaderTrigger'

//FUNCTION
const BrandCard = ({ isChanged, setIsChanged, editData, setEditData }) => {
    //#region code
    const dispatch = useDispatch();
    const { company_id } = useSelector((state) => state.GlobalVariables);
    const [formData, setFormData] = useState({
        status: true,
        isPrimary: false
    })
    const [isEdit, setIsEdit] = useState(false)
    const [validationErrors, setValidationErrors] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [title, setTitle] = useState('Alert');
    const [dialogContent, setDialogContent] = useState('');
    const [buttonType, setButtonType] = useState(null);

    const handleOpenDialog = (heading, content, type) => {
        setTitle(heading);
        setDialogContent(content);
        setButtonType(type);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setTitle('Alert');
        setDialogContent('');
        setButtonType(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target

        //Update form data
        setFormData((state) => ({
            ...state,
            [name]: value
        }));

        // Reset the validation error when the user starts typing
        if (validationErrors[name]) {
            setValidationErrors((prevValidationError) => ({
                ...prevValidationError,
                [name]: '',
            }));
        }
    }

    const handleChangeCheckbox = (e) => {
        const { name, checked } = e.target
        setFormData((state) => ({
            ...state,
            [name]: checked
        }))
    }

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();
            
            dispatch(triggerLoader());

            const error = handleFormValidation();
            if (error === false) {
                return false;
            }
            setValidationErrors({});

            const dataToSend = {
                ...formData,
                company_id: company_id
            }

            if (!isEdit) {
                const res = await configServ.addBrand(dataToSend);
                if (res.status === 200) {
                    setIsChanged(!isChanged);
                    clear();
                    handleOpenDialog('Success', res.message, 'success');
                }
                else if (res.status === 400) {
                    handleOpenDialog('Warning', res.message || "Failed to create brand.", 'warning');
                }
                else {
                    handleOpenDialog('Error', res.message || "Failed to create brand.", 'error');
                }
            }
            else {
                const res = await configServ.editBrand(dataToSend);
                if (res.status === 200) {
                    setIsChanged(!isChanged);
                    clear();
                    handleOpenDialog('Success', res.message, 'success');
                }
                else {
                    handleOpenDialog('Error', res.message || "Failed to update brand.", 'error');
                }
            }
        }
        catch (e) {
            console.log(e);
            handleOpenDialog('Error', "An unexpected error has occurred.", 'error');
        }
        finally {
            dispatch(triggerLoader());
        }
    }

    useEffect(() => {
        if (Object.keys(editData).length !== 0) {
            setFormData(editData)
            setIsEdit(true)
            setFormData((prev) => ({
                ...prev,
                status: true
            }));
        }
    }, [editData]);

    const clear = () => {
        setFormData({
            status: true
        })
        setValidationErrors({})
        setIsEdit(false);
        setEditData({});
    }

    const handleFormValidation = () => {
        const errors = {}
        if (!formData.brand_name) {
            errors.brand_name = "required";
        }
        setValidationErrors(errors);
        return Object.values(errors).every((error) => error === "");
    }
    //#endregion

    return (
        <>
            <AlertDialog
                open={dialogOpen}
                setOpen={handleCloseDialog}
                title={title}
                message={dialogContent}
                buttonTitle="Ok"
                buttonType={buttonType}
            />

            <motion.div style={{ width: "100%", backgroundColor: "#e3f3fc", padding: 20, borderRadius: '10px', border: 'none' }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.1, delay: 0.1 }}
            >
                <Grid container alignItems="flex-end" justifyContent="flex-start" spacing={1}>
                    <Grid item xs={12} md={12} justifyContent={'flex-start'}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 'bolder' }}>Brand Detail</Typography>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            id="brand_name"
                            name="brand_name"
                            label="Brand Name*"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={formData.brand_name || ''}
                            onChange={handleChange}
                            error={validationErrors.brand_name}
                            sx={{ backgroundColor: 'white' }}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            id="description"
                            name="description"
                            label="Description"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={formData.description || ''}
                            onChange={handleChange}
                            sx={{ backgroundColor: 'white' }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormGroup sx={{ display: 'flex', flexDirection: 'row' }}>
                            <FormControlLabel
                                name='status'
                                control={<Checkbox checked={Boolean(formData.status) || false} />}
                                onChange={handleChangeCheckbox}
                                label="Status"
                                style={{ marginRight: '20px' }}
                            />
                            <FormControlLabel
                                name='isPrimary'
                                control={<Checkbox checked={Boolean(formData.isPrimary) || false} />}
                                onChange={handleChangeCheckbox}
                                label="Is Primary"
                            />
                        </FormGroup>
                    </Grid>

                </Grid>

                <Grid container spacing={2} justifyContent="space-between" px={2}>
                    <Grid
                        item
                        container
                        justifyContent="flex-end"
                        alignItems="center"
                        spacing={2}
                        mt={1}
                    >
                        <Stack direction={'row'} spacing={2}>
                            <Button
                                onClick={clear}
                                variant="contained"
                                color='secondary'
                                startIcon={<RotateLeftIcon />}
                            >
                                Reset
                            </Button>
                        {/* </Grid>
                        <Grid item> */}
                            <Button
                                onClick={handleSubmit}
                                autoFocus
                                variant="contained"
                                color={isEdit ? 'success' : 'primary'}
                                startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
                            >
                                {isEdit ? "Update" : "Save"}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </motion.div>

        </>
    )
}

export default memo(BrandCard);

